import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Autocomplete,
  Box,
  Chip,
  Stack,
  TextField,
  Typography,
  useTokens,
} from '@barracuda-internal/bds-core';
import { actions as globalActions } from '../global/globalReducer';
import { actions as peekActions } from './adminPeekReducer';

/**
 * Inline picker shown on tenant-scoped pages (Dashboard, Scans) for AI
 * Guardian admins. Selecting an account injects `?as_account=N` on every
 * subsequent account-scoped request so the admin sees that account's data
 * without changing their AccountSwitcher.
 */
const AdminAccountPicker = () => {
  const tokens = useTokens();
  const dispatch = useDispatch();
  // In the support SPA all authenticated users are support staff — always show the picker.
  const isAdmin = useSelector((state) => state.auth.isAuthenticated);
  const accounts = useSelector(
    (state) => state.global.accounts.items || []
  );
  const accountsLoading = useSelector(
    (state) => state.global.accounts.isLoading
  );
  const peek = useSelector((state) => state.adminPeek);

  useEffect(() => {
    if (isAdmin && accounts.length === 0 && !accountsLoading) {
      dispatch(globalActions.fetchAccounts());
    }
  }, [isAdmin, accounts.length, accountsLoading, dispatch]);

  const options = useMemo(
    () =>
      accounts
        .filter((a) => a.ai_guardian_enabled !== false)
        .map((a) => ({ id: a.id, label: a.name })),
    [accounts]
  );

  const value = useMemo(
    () =>
      peek.accountId
        ? { id: peek.accountId, label: peek.accountName || `Account ${peek.accountId}` }
        : null,
    [peek]
  );

  if (!isAdmin) return null;

  return (
    <Stack
      direction="row"
      sx={{ alignItems: 'center', gap: tokens.aliasesSpaceComponentDense }}
    >
      <Autocomplete
        size="small"
        sx={{ width: 280 }}
        options={options}
        value={value}
        loading={accountsLoading}
        isOptionEqualToValue={(opt, v) => opt.id === v?.id}
        onChange={(_event, next) => {
          if (next) {
            dispatch(peekActions.setPeekAccount(next.id, next.label));
          } else {
            dispatch(peekActions.clearPeekAccount());
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="View as account"
            placeholder="Default"
            bdsSize="small"
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Stack sx={{ gap: 0 }}>
              <Typography variant="body1">{option.label}</Typography>
              <Typography variant="captionSubtle">{option.id}</Typography>
            </Stack>
          </Box>
        )}
      />
      {peek.accountId && (
        <Chip
          bdsType="warningSubtle"
          bdsSize="medium"
          label="Peek mode"
        />
      )}
    </Stack>
  );
};

export default AdminAccountPicker;
